import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { Customer, Plan } from '@/types';
import { getFieldClassName, calculateMaxUsers, getMaxUsersLimit, generateServiceUrl } from '@/utils/customerUtils';

interface CustomerEditFormProps {
  open: boolean;
  editingCustomer: Customer;
  originalCustomer: Customer;
  plans: Plan[];
  mainDomain: string;
  loading: boolean;
  onCustomerChange: (customer: Customer) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export const CustomerEditForm: React.FC<CustomerEditFormProps> = ({
  open,
  editingCustomer,
  originalCustomer,
  plans,
  mainDomain,
  loading,
  onCustomerChange,
  onSubmit,
  onCancel,
}) => {
  return (
    <Dialog open={open}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" onClose={onCancel}>
        <DialogHeader>
          <DialogTitle>고객사 정보 수정</DialogTitle>
          <DialogDescription>변경된 필드는 노란색으로 표시됩니다</DialogDescription>
        </DialogHeader>
        <div className="mt-4">
        <form onSubmit={onSubmit} className="space-y-6">
          <div className="border border-gray-300 rounded-lg p-6 bg-gray-50">
            <div className="border-b border-gray-300 pb-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">메인 정보</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_customer_code">고객사 코드 *</Label>
                <Input
                  id="edit_customer_code"
                  value={editingCustomer.customer_code}
                  onChange={(e) => onCustomerChange({...editingCustomer, customer_code: e.target.value})}
                  className={getFieldClassName('customer_code', editingCustomer, originalCustomer)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_alias">Alias</Label>
                <Input
                  id="edit_alias"
                  value={editingCustomer.alias || ''}
                  onChange={(e) => onCustomerChange({...editingCustomer, alias: e.target.value})}
                  className={getFieldClassName('alias', editingCustomer, originalCustomer)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_customer_name">회사명 *</Label>
                <Input
                  id="edit_customer_name"
                  value={editingCustomer.customer_name}
                  onChange={(e) => onCustomerChange({...editingCustomer, customer_name: e.target.value})}
                  className={getFieldClassName('customer_name', editingCustomer, originalCustomer)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_plan_id">계정 유형 *</Label>
                <Select
                  value={editingCustomer.plan?.id ? String(editingCustomer.plan.id) : ''}
                  onValueChange={(value) => {
                    const planId = parseInt(value);
                    const selectedPlan = plans.find(p => p.id === planId);
                    const planName = selectedPlan?.plan_name.toLowerCase();
                    const maxUsers = calculateMaxUsers(planName, editingCustomer.max_users);
                    onCustomerChange({
                      ...editingCustomer,
                      plan: selectedPlan || editingCustomer.plan,
                      max_users: maxUsers
                    });
                  }}
                  required
                >
                  <SelectTrigger className={getFieldClassName('plan_id', editingCustomer, originalCustomer)}>
                    <SelectValue placeholder="플랜 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans.map(plan => (
                      <SelectItem key={plan.id} value={String(plan.id)}>
                        {plan.plan_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_max_users">최대 사용자 수 *</Label>
                <Input
                  id="edit_max_users"
                  type="number"
                  value={editingCustomer.max_users}
                  onChange={(e) => {
                    const plan = editingCustomer.plan;
                    const planName = plan?.plan_name.toLowerCase();
                    const limits = getMaxUsersLimit(planName);
                    let maxUsers = parseInt(e.target.value) || 1;
                    maxUsers = Math.max(limits.min, maxUsers);
                    if (limits.max !== null) {
                      maxUsers = Math.min(maxUsers, limits.max);
                    }
                    onCustomerChange({...editingCustomer, max_users: maxUsers});
                  }}
                  className={getFieldClassName('max_users', editingCustomer, originalCustomer)}
                  min="1"
                  required
                  disabled={editingCustomer.plan?.plan_name.toLowerCase() === 'personal'}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_service_url">서비스 URL</Label>
                <Input
                  id="edit_service_url"
                  value={generateServiceUrl(editingCustomer.customer_code, editingCustomer.alias || '', mainDomain)}
                  readOnly
                  className="bg-gray-100"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_contract_end_date">계약 종료일 *</Label>
                <Input
                  id="edit_contract_end_date"
                  type="date"
                  value={editingCustomer.contract_end_date ? editingCustomer.contract_end_date.split('T')[0] : ''}
                  onChange={(e) => onCustomerChange({...editingCustomer, contract_end_date: e.target.value})}
                  className={getFieldClassName('contract_end_date', editingCustomer, originalCustomer)}
                  required
                />
              </div>
            </div>
          </div>

          <div className="border border-gray-300 rounded-lg p-6 bg-gray-50">
            <div className="border-b border-gray-300 pb-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">담당자 정보</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_contact_name">담당자 이름</Label>
                <Input
                  id="edit_contact_name"
                  value={editingCustomer.contact_name || ''}
                  onChange={(e) => onCustomerChange({...editingCustomer, contact_name: e.target.value})}
                  className={getFieldClassName('contact_name', editingCustomer, originalCustomer)}
                  placeholder="선택사항"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_contact_email">담당자 이메일</Label>
                <Input
                  id="edit_contact_email"
                  type="email"
                  value={editingCustomer.contact_email || ''}
                  onChange={(e) => onCustomerChange({...editingCustomer, contact_email: e.target.value})}
                  className={getFieldClassName('contact_email', editingCustomer, originalCustomer)}
                  placeholder="선택사항"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_contact_phone">담당자 연락처</Label>
                <Input
                  id="edit_contact_phone"
                  value={editingCustomer.contact_phone || ''}
                  onChange={(e) => onCustomerChange({...editingCustomer, contact_phone: e.target.value})}
                  className={getFieldClassName('contact_phone', editingCustomer, originalCustomer)}
                  placeholder="선택사항"
                />
              </div>
            </div>
          </div>

          <div className="border border-gray-300 rounded-lg p-6 bg-gray-50">
            <div className="border-b border-gray-300 pb-3 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">기타 정보</h3>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_notes">비고</Label>
              <textarea
                id="edit_notes"
                value={editingCustomer.notes || ''}
                onChange={(e) => onCustomerChange({...editingCustomer, notes: e.target.value})}
                className={getFieldClassName('notes', editingCustomer, originalCustomer, true)}
                rows={4}
              />
            </div>
          </div>
          <div className="flex space-x-2 pt-4 border-t">
            <Button type="submit" disabled={loading}>
              {loading ? '저장 중...' : '저장'}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
            >
              취소
            </Button>
          </div>
        </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

